import { useCallback } from "react";

import useLocalStorageState from "use-local-storage-state";

/** localStorage key holding the most-recent "Tell AI what to write" instructions. */
export const INSTRUCTION_HISTORY_KEY = "generate-bar-instruction-history";

/** How many past instructions are kept. */
export const INSTRUCTION_HISTORY_LIMIT = 10;

/**
 * Persists the last {@link INSTRUCTION_HISTORY_LIMIT} "Tell AI what to write"
 * instructions in localStorage so the GenerateBar can offer them as autocomplete
 * suggestions. Purely frontend — nothing is sent to the API.
 *
 * Newest-first. `addInstruction` trims, ignores blanks, and de-dupes
 * case-insensitively so re-using an instruction floats it back to the top
 * instead of creating a duplicate.
 */
export const useInstructionHistory = () => {
	const [history, setHistory] = useLocalStorageState<string[]>(INSTRUCTION_HISTORY_KEY, {
		defaultValue: [],
	});

	const addInstruction = useCallback(
		(value: string) => {
			const trimmed = value.trim();

			if (!trimmed) {
				return;
			}

			setHistory((previous) => {
				const withoutDuplicate = previous.filter(
					(entry) => entry.toLowerCase() !== trimmed.toLowerCase()
				);

				return [trimmed, ...withoutDuplicate].slice(0, INSTRUCTION_HISTORY_LIMIT);
			});
		},
		[setHistory]
	);

	return { history, addInstruction };
};

/**
 * Suggestions to show for the current input: a case-insensitive substring match,
 * or the full history when the field is empty. An exact (case-insensitive) match
 * is hidden so the dropdown never just echoes back what's already typed.
 */
export const filterInstructions = (history: string[], query: string): string[] => {
	const trimmed = query.trim().toLowerCase();

	if (!trimmed) {
		return history;
	}

	return history.filter((entry) => {
		const lower = entry.toLowerCase();

		return lower.includes(trimmed) && lower !== trimmed;
	});
};
